import { useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Truck, MapPin, PackageCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { orderMode, hostOf, fmtDT } from "./orderMeta";
import { OrderTimeline, ShipmentHistory } from "./OrderTimeline";
import { AddressForm, AddressBlock } from "./AddressForm";

export function TrackingLink({ o, testid }) {
  const sh = o.shipment;
  if (!sh?.carrier) return null;
  return (
    <div data-testid={testid || `tracking-${o.order_number}`} className="text-[11px] text-zinc-700">
      <div className="flex items-center gap-1"><Truck size={12} /> <span className="font-semibold">{sh.carrier_name}</span>{sh.tracking_number && <span data-testid={`tracking-number-${o.order_number}`}> · nº <span className="font-mono">{sh.tracking_number}</span></span>}</div>
      {sh.shipped_at && <div className="text-zinc-500">Enviado em {fmtDT(sh.shipped_at)}</div>}
      {sh.carrier === "own_delivery" && <div className="text-zinc-500">Entrega feita pela própria loja — sem rastreio.</div>}
      {sh.tracking_url && (
        <a href={sh.tracking_url} target="_blank" rel="noopener noreferrer" data-testid={`track-btn-${o.order_number}`}
           className="mt-1 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-black text-white font-semibold hover:bg-[#c8102e] transition-colors">
          Acompanhar encomenda <ExternalLink size={11} /> <span className="text-white/60 font-normal">{hostOf(sh.tracking_url)}</span>
        </a>
      )}
      {sh.note && <div className="text-zinc-500 mt-1">Nota da loja: {sh.note}</div>}
      <p className="text-[10px] text-zinc-400 mt-1">O estado do transporte é indicado pela loja; o detalhe está no site da transportadora.</p>
    </div>
  );
}

export function BuyerOrderPanel({ o, reload }) {
  const { confirm } = useConfirm();
  const [editAddr, setEditAddr] = useState(false);
  const mode = orderMode(o);
  const paidish = ["paid", "partially_refunded"].includes(o.payment_status);
  if (!paidish && o.status !== "completed") return null;
  const canEditAddr = mode === "delivery" && ["paid", "processing"].includes(o.status);
  const missing = mode === "delivery" && o.shipping_address_status === "missing";
  const confirmFrom = mode === "delivery" ? "shipped" : "ready";

  const confirmReceipt = async () => {
    const ok = await confirm({ title: mode === "delivery" ? "Confirmar que recebeste a encomenda?" : "Confirmar que levantaste a encomenda?", description: "Esta ação fica registada e não pode ser repetida.", confirmLabel: "Confirmar" });
    if (!ok) return;
    try { await api.post(`/orders/${o.id}/status`, { status: "completed" }); toast.success(mode === "delivery" ? "Receção confirmada" : "Levantamento confirmado"); reload?.(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="mt-2 pt-2 border-t border-zinc-100 space-y-2">
      <OrderTimeline o={o} />
      {mode === "delivery" && (
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-1 min-w-0"><MapPin size={12} className="mt-0.5 shrink-0 text-zinc-400" /><AddressBlock o={o} testid={`buyer-address-${o.order_number}`} /></div>
          {canEditAddr && !editAddr && <button data-testid={`address-edit-${o.order_number}`} onClick={() => setEditAddr(true)} className="text-[11px] font-semibold underline shrink-0">{missing ? "Adicionar morada" : "Corrigir morada"}</button>}
        </div>
      )}
      {missing && !editAddr && <p data-testid={`address-missing-${o.order_number}`} className="text-[11px] text-orange-800 bg-orange-50 border border-orange-200 rounded-lg p-2">A loja só pode enviar depois de teres a morada confirmada.</p>}
      {editAddr && <AddressForm order={o} onDone={() => { setEditAddr(false); reload?.(); }} onCancel={() => setEditAddr(false)} />}
      {mode === "delivery" && <TrackingLink o={o} />}
      {mode === "pickup" && o.status === "ready" && <p className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-2">Pronta para levantar na loja. Depois de levantares, confirma aqui.</p>}
      {o.status === confirmFrom && (
        <button data-testid={`confirm-receipt-${o.order_number}`} onClick={confirmReceipt} className="w-full py-2 rounded-lg border border-black text-xs font-bold hover:bg-black hover:text-white transition-colors flex items-center justify-center gap-1">
          <PackageCheck size={13} /> {mode === "delivery" ? "Confirmar receção" : "Confirmar levantamento"}
        </button>
      )}
      {o.status === "completed" && o.confirmed_by && <p data-testid={`confirmed-${o.order_number}`} className="text-[11px] text-zinc-500">Confirmado por {o.confirmed_by === "buyer" ? "ti" : "a loja"} em {fmtDT(o.confirmed_at)}.</p>}
      <ShipmentHistory o={o} />
    </div>
  );
}
